import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Github, Loader2, User, ExternalLink, KeyRound, Eye, EyeOff } from "lucide-react";
import AppHeader from "../components/AppHeader";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import NotificationsCard from "../components/notify/NotificationsCard";

const inputCls = "w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-800 outline-none focus:border-sky-400";
const Card = ({ icon: Icon, title, desc, children, testid }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7" data-testid={testid}>
    <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900"><Icon size={17} className="text-emerald-600" /> {title}</h2>
    {desc && <p className="mt-1 text-sm text-slate-500">{desc}</p>}
    <div className="mt-5">{children}</div>
  </section>
);

const Profile = () => {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.updateProfile(form); await refresh(); toast.success("Profile saved"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card icon={User} title="Profile" testid="settings-profile">
      <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
        <label className="space-y-1.5"><span className="text-xs text-slate-500">Name</span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="settings-name-input" /></label>
        <label className="space-y-1.5"><span className="text-xs text-slate-500">Phone (used to prefill payments)</span><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="settings-phone-input" /></label>
        <label className="space-y-1.5 sm:col-span-2"><span className="text-xs text-slate-500">Email</span><input value={user?.email || ""} disabled className={`${inputCls} opacity-60`} /></label>
        <button type="submit" disabled={busy} data-testid="settings-save-btn" className="w-fit h-10 px-5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 flex items-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Save</button>
      </form>
    </Card>
  );
};

const PasswordChange = () => {
  const { changePassword } = useAuth();
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("New password must be at least 6 characters");
    setBusy(true);
    try { await changePassword(pw); setPw(""); toast.success("Password changed — other devices were logged out"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card icon={KeyRound} title="Password" desc="Type a new password and save. Other devices will be logged out." testid="settings-password">
      <form onSubmit={save} className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (at least 6 characters)" autoComplete="new-password" className={`${inputCls} pr-10`} data-testid="settings-new-password-input" />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900" aria-label="Toggle password visibility" data-testid="settings-password-toggle">{show ? <EyeOff size={15} /> : <Eye size={15} />}</button>
        </div>
        <button type="submit" disabled={busy} data-testid="settings-change-password-btn" className="h-10 px-5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 disabled:opacity-60 flex items-center justify-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Change password</button>
      </form>
    </Card>
  );
};

const GithubConnect = () => {
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.githubStatus().then(setStatus).catch(() => setStatus({ connected: false })); }, []);
  const connect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.githubConnect(token.trim()); setStatus(r); setToken(""); toast.success(`Connected as @${r.login}`); } catch (err) { toast.error(errMsg(err, "Token rejected by GitHub")); } finally { setBusy(false); }
  };
  const disconnect = async () => { await api.githubDisconnect(); setStatus({ connected: false }); toast.success("GitHub disconnected"); };
  return (
    <Card icon={Github} title="GitHub" desc="Push your projects to your own repositories." testid="settings-github">
      {status?.connected ? (
        <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
          <span className="text-sm text-slate-600">Connected as <span className="font-mono text-slate-900" data-testid="github-login">@{status.login}</span></span>
          <button onClick={disconnect} data-testid="github-disconnect-btn" className="h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-600 hover:text-rose-600 hover:border-rose-300">Disconnect</button>
        </div>
      ) : (
        <form onSubmit={connect} className="space-y-3">
          <ol className="text-sm text-slate-500 list-decimal pl-5 space-y-1">
            <li>Open <a href="https://github.com/settings/personal-access-tokens/new" target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline inline-flex items-center gap-1">GitHub → Fine-grained tokens <ExternalLink size={11} /></a></li>
            <li>Allow access to the repositories you want, with <b className="text-slate-600">Contents: Read & write</b> and <b className="text-slate-600">Administration: Read & write</b> (to create new repos).</li>
            <li>Paste the token below. It's stored encrypted and never shown again.</li>
          </ol>
          <div className="flex gap-2">
            <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="github_pat_…" autoComplete="off" data-testid="github-token-input" className={inputCls} />
            <button type="submit" disabled={busy || token.length < 20} data-testid="github-connect-btn" className="h-10 px-5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 disabled:opacity-50 flex items-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />} Connect</button>
          </div>
        </form>
      )}
    </Card>
  );
};

export default function Settings() {
  return (
    <div className="min-h-screen bg-[#F5F8FC]" data-testid="settings-page">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-6">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Settings</h1>
        <Profile />
        <PasswordChange />
        <NotificationsCard Card={Card} />
        <GithubConnect />
      </main>
    </div>
  );
}
